(() => {
  'use strict';

  const SAVED_KEY = 'regista_saved_products_v1';
  const EMAIL_KEY = 'regista_saved_email_v1';
  const PENDING_DRIP_KEY = 'regista_pending_drip_events_v1';
  let activeProduct = null;
  let lastFocusedElement = null;

  const readJson = (value, fallback) => {
    try {
      return JSON.parse(value) ?? fallback;
    } catch (error) {
      return fallback;
    }
  };

  const getSavedProducts = () => {
    try {
      const value = readJson(localStorage.getItem(SAVED_KEY), []);
      return Array.isArray(value) ? value : [];
    } catch (error) {
      return [];
    }
  };

  const setSavedProducts = (products) => {
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(products.slice(0, 100)));
    } catch (error) {
      // Storage can be unavailable in private browsing. The current session still works.
    }
  };

  const getSavedEmail = () => {
    try {
      return localStorage.getItem(EMAIL_KEY) || '';
    } catch (error) {
      return '';
    }
  };

  const setSavedEmail = (email) => {
    try {
      localStorage.setItem(EMAIL_KEY, email);
    } catch (error) {
      // Ignore storage errors after a successful form submission.
    }
  };

  const productFromButton = (button) => ({
    id: String(button.dataset.productId || ''),
    variantId: String(button.dataset.variantId || ''),
    handle: button.dataset.productHandle || '',
    title: button.dataset.productTitle || '',
    url: button.dataset.productUrl || '',
    image: button.dataset.productImage || '',
    team: button.dataset.productTeam || '',
    size: button.dataset.productSize || '',
    priceCents: Number(button.dataset.productPrice || 0),
    currency: button.dataset.productCurrency || '',
    savedAt: new Date().toISOString()
  });

  const isSaved = (productId) => getSavedProducts().some((product) => String(product.id) === String(productId));

  const updateSaveButtons = (root = document) => {
    root.querySelectorAll('[data-regista-save-product]').forEach((button) => {
      const saved = isSaved(button.dataset.productId);
      button.classList.toggle('is-saved', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      const label = button.querySelector('[data-regista-save-label]');
      if (label) label.textContent = saved ? 'Saved to watchlist' : 'Save to watchlist';
      button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'shirt'}${saved ? ' from watchlist' : ' to watchlist'}`);
    });
  };

  const addSavedProduct = (product) => {
    const products = getSavedProducts().filter((item) => String(item.id) !== String(product.id));
    products.unshift(product);
    setSavedProducts(products);
    updateSaveButtons();
    document.dispatchEvent(new CustomEvent('regista:saved-products-change', { detail: { products } }));
  };

  const removeSavedProduct = (productId) => {
    const products = getSavedProducts().filter((item) => String(item.id) !== String(productId));
    setSavedProducts(products);
    updateSaveButtons();
    document.dispatchEvent(new CustomEvent('regista:saved-products-change', { detail: { products } }));
  };

  const getModal = () => document.querySelector('[data-regista-save-modal]');

  const setModalMessage = (message, isError = false) => {
    const modal = getModal();
    const box = modal?.querySelector('[data-regista-save-message]');
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
    box.classList.toggle('is-error', isError);
  };

  const openModal = (product) => {
    const modal = getModal();
    if (!modal) return;

    activeProduct = product;
    lastFocusedElement = document.activeElement;
    const image = modal.querySelector('[data-regista-save-image]');
    const title = modal.querySelector('[data-regista-save-title]');
    const meta = modal.querySelector('[data-regista-save-meta]');
    const email = modal.querySelector('[data-regista-save-email]');
    const tags = modal.querySelector('[data-regista-save-tags]');

    if (image) {
      image.src = product.image || '';
      image.alt = product.title || '';
      image.hidden = !product.image;
    }
    if (title) title.textContent = product.title || 'Saved shirt';
    if (meta) meta.textContent = [product.team, product.size ? `Size ${product.size}` : ''].filter(Boolean).join(' | ');
    if (email && getSavedEmail()) email.value = getSavedEmail();
    if (tags) {
      const safeHandle = String(product.handle || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 120);
      tags.value = `newsletter,regista_saved_shirt,regista_product_${product.id}${safeHandle ? `,regista_handle_${safeHandle}` : ''}`;
    }

    setModalMessage('');
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    document.body.classList.add('regista-save-modal-open');
    requestAnimationFrame(() => {
      const emailField = modal.querySelector('[data-regista-save-email]');
      const dialog = modal.querySelector('[data-regista-save-dialog]');
      if (emailField instanceof HTMLElement) emailField.focus();
      else if (dialog instanceof HTMLElement) dialog.focus();
    });
  };

  const closeModal = () => {
    const modal = getModal();
    if (!modal) return;
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('regista-save-modal-open');
    activeProduct = null;
    if (lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus({ preventScroll: true });
  };


  const dripIsLive = () => {
    if (getModal()?.dataset.dripLive === 'true') return true;
    return Array.from(document.scripts || []).some((script) => /getdrip|tag\.getdrip|drip\.com/i.test(script.src || ''));
  };

  const getPendingDripEvents = () => {
    try {
      const events = readJson(localStorage.getItem(PENDING_DRIP_KEY), []);
      return Array.isArray(events) ? events : [];
    } catch (error) {
      return [];
    }
  };

  const setPendingDripEvents = (events) => {
    try {
      localStorage.setItem(PENDING_DRIP_KEY, JSON.stringify(events.slice(-100)));
    } catch (error) {
      // If storage is unavailable, the live page queue still works when Drip is connected.
    }
  };

  const pushDripRequest = (request) => {
    window._dcq = window._dcq || [];
    window._dcq.push(request);
  };

  const flushPendingDripEvents = () => {
    if (!dripIsLive()) return;
    const pending = getPendingDripEvents();
    if (!pending.length) return;
    pending.forEach(pushDripRequest);
    setPendingDripEvents([]);
  };

  const queueDrip = (request) => {
    if (dripIsLive()) {
      pushDripRequest(request);
      return;
    }
    const pending = getPendingDripEvents();
    pending.push(request);
    setPendingDripEvents(pending);
  };

  const trackDripSave = (product, email) => {
    if (!product || !email) return;

    const dripTags = ['Regista Watchlist'];
    if (product.team) dripTags.push(`Regista Team: ${product.team}`);
    if (product.size) dripTags.push(`Regista Size: ${product.size}`);

    queueDrip(['identify', {
      email,
      tags: dripTags,
      eu_consent: 'granted',
      eu_consent_message: 'Email me about saved shirts, matching drops and relevant Regista updates.',
      regista_watchlist_opt_in: true,
      regista_last_saved_product_id: product.id,
      regista_last_saved_variant_id: product.variantId,
      regista_last_saved_product: product.title,
      regista_last_saved_product_url: product.url,
      regista_last_saved_team: product.team,
      regista_last_saved_size: product.size
    }]);

    queueDrip(['track', 'Regista Saved Shirt', {
      product_id: product.id,
      product_variant_id: product.variantId || product.id,
      name: product.title,
      product_url: product.url,
      image_url: product.image,
      categories: product.team || 'Football Shirts',
      size: product.size,
      price: Number(product.priceCents || 0) / 100,
      currency: product.currency || '',
      source: 'Shopify',
      watchlist_source: 'Regista Theme',
      team: product.team || '',
      product_handle: product.handle || '',
      marketing_consent: true,
      occurred_at: new Date().toISOString()
    }]);
  };

  const trackDripRemove = (product, email) => {
    if (!product || !email) return;
    queueDrip(['track', 'Regista Removed Saved Shirt', {
      product_id: product.id,
      product_variant_id: product.variantId || product.id,
      name: product.title,
      product_url: product.url,
      size: product.size,
      source: 'Shopify',
      occurred_at: new Date().toISOString()
    }]);
  };

  const registerWatchlistEmailWithShopify = async (product, email) => {
    const captureForm = document.getElementById('RegistaSaveCaptureForm');
    if (!(captureForm instanceof HTMLFormElement)) return true;

    const emailField = captureForm.querySelector('[data-regista-save-capture-email]');
    const tagsField = captureForm.querySelector('[data-regista-save-capture-tags]');
    if (emailField) emailField.value = email;
    if (tagsField) {
      const safeHandle = String(product.handle || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 120);
      tagsField.value = [
        'newsletter',
        'regista_watchlist',
        'regista_saved_shirt',
        product.id ? `regista_product_${product.id}` : '',
        safeHandle ? `regista_handle_${safeHandle}` : ''
      ].filter(Boolean).join(',');
    }

    try {
      const response = await fetch(captureForm.action, {
        method: 'POST',
        body: new FormData(captureForm),
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        redirect: 'follow'
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  const handleSaveClick = async (button) => {
    if (button.getAttribute('aria-busy') === 'true') return;

    const product = productFromButton(button);
    if (!product.id) return;

    if (isSaved(product.id)) {
      const savedProduct = getSavedProducts().find((item) => String(item.id) === String(product.id)) || product;
      removeSavedProduct(product.id);
      trackDripRemove(savedProduct, getSavedEmail());
      return;
    }

    const savedEmail = getSavedEmail();
    if (savedEmail) {
      button.setAttribute('aria-busy', 'true');
      try {
        addSavedProduct(product);
        trackDripSave(product, savedEmail);
        registerWatchlistEmailWithShopify(product, savedEmail).catch(() => false);
      } finally {
        button.removeAttribute('aria-busy');
      }
      return;
    }

    openModal(product);
  };

  const handleSaveForm = async (form) => {
    if (!activeProduct) return;
    const emailInput = form.querySelector('[data-regista-save-email]');
    const consentInput = form.querySelector('[data-regista-save-consent]');
    const submit = form.querySelector('button[type="submit"]');
    const email = emailInput?.value.trim() || '';

    if (!email || !emailInput.checkValidity()) {
      emailInput?.reportValidity();
      return;
    }

    if (consentInput && !consentInput.checked) {
      consentInput.reportValidity();
      return;
    }

    const product = activeProduct;
    submit.disabled = true;
    submit.setAttribute('aria-busy', 'true');
    setModalMessage('Saving to your watchlist...');

    setSavedEmail(email);
    addSavedProduct(product);
    trackDripSave(product, email);

    // Shopify contact capture is a secondary sync. A temporary form/network failure must never
    // prevent the shopper from saving the shirt or queuing the Drip event.
    registerWatchlistEmailWithShopify(product, email).catch(() => false);

    setModalMessage('Saved to your watchlist.');
    window.setTimeout(closeModal, 1050);
    window.setTimeout(() => {
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
    }, 1150);
  };

  const updateScarcity = (event) => {
    const variant = event.detail?.variant;
    if (!variant?.id) return;

    document.querySelectorAll('[data-regista-product-cro]').forEach((container) => {
      if (event.detail?.formId && container.dataset.formId && event.detail.formId !== container.dataset.formId) return;
      const dataNode = container.querySelector('[data-regista-variant-inventory]');
      const inventory = readJson(dataNode?.textContent || '{}', {});
      const variantInventory = inventory[String(variant.id)];
      const scarcity = container.querySelector('[data-regista-product-scarcity]');
      if (scarcity && variantInventory) {
        scarcity.hidden = !(variantInventory.available && variantInventory.tracked && !variantInventory.continue && Number(variantInventory.quantity) === 1);
      }
      container.querySelectorAll('[data-regista-save-product]').forEach((button) => {
        button.dataset.variantId = String(variant.id);
        if (typeof variant.price !== 'undefined') button.dataset.productPrice = String(variant.price || 0);
      });
    });
  };

  let registaDetectedCountry = null;
  let registaDeliveryDetectionPromise = null;
  const EUROPE_COUNTRY_CODES = new Set([
    'AL', 'AD', 'AM', 'AT', 'AZ', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'GE', 'DE', 'GR', 'HU',
    'IS', 'IE', 'IT', 'KZ', 'XK', 'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK', 'NO', 'PL', 'PT', 'RO', 'RU',
    'SM', 'RS', 'SK', 'SI', 'ES', 'SE', 'CH', 'TR', 'UA', 'VA'
  ]);

  // Delivery estimates intentionally use Shopify's IP/browser detected country as the
  // primary source. localization.country can represent a previously selected Market and
  // therefore must only be a server-rendered fallback for this shipping ETA component.
  const getDetectedCountryCache = () => registaDetectedCountry;

  const setDetectedCountryCache = (country) => {
    registaDetectedCountry = country || null;
  };

  const addBusinessDays = (startDate, businessDays) => {
    const date = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 12, 0, 0);
    let remaining = Math.max(0, Number(businessDays || 0));
    while (remaining > 0) { date.setDate(date.getDate() + 1); const day = date.getDay(); if (day !== 0 && day !== 6) remaining -= 1; }
    return date;
  };

  const formatDeliveryDate = (days) => {
    const arrival = addBusinessDays(new Date(), days);
    const locale = document.documentElement.lang || 'en-GB';
    const now = new Date();
    const options = { day: 'numeric', month: 'short' };
    if (arrival.getFullYear() !== now.getFullYear()) options.year = 'numeric';
    try { return new Intl.DateTimeFormat(locale, options).format(arrival); }
    catch (error) { return arrival.toLocaleDateString('en-GB', options); }
  };

  const updateDeliveryDateCopy = (node, days) => {
    const dateNode = node.querySelector('[data-regista-delivery-date]');
    const daysNode = node.querySelector('.regista-delivery-estimate__days');
    const safeDays = Number(days || node.dataset.deliveryDays || 20);
    if (dateNode) dateNode.textContent = `Estimated by ${formatDeliveryDate(safeDays)}`;
    if (daysNode) daysNode.textContent = ` · up to ${safeDays} business days`;
  };

  const updateAllDeliveryDates = (root = document) => {
    root.querySelectorAll('[data-regista-delivery-estimate]').forEach((node) => updateDeliveryDateCopy(node, Number(node.dataset.deliveryDays || 20)));
  };

  const deliveryDaysForCountry = (code, node) => {
    const normalized = String(code || '').toUpperCase();
    if (normalized === 'GB') return Number(node.dataset.shipUk || 10);
    if (normalized === 'US') return Number(node.dataset.shipUs || 10);
    if (EUROPE_COUNTRY_CODES.has(normalized)) return Number(node.dataset.shipEu || 12);
    return Number(node.dataset.shipRow || 20);
  };

  const normalizeDetectedCountry = (raw) => {
    const handle = String(raw?.handle || raw?.iso_code || raw?.code || '').trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(handle)) return null;
    return { handle, name: String(raw?.name || handle).trim() || handle };
  };

  const applyDetectedDeliveryCountry = (country, root = document) => {
    const normalizedCountry = normalizeDetectedCountry(country);
    if (!normalizedCountry) return false;

    const code = normalizedCountry.handle;
    const name = normalizedCountry.name;
    let updated = false;

    root.querySelectorAll('[data-regista-delivery-estimate]').forEach((node) => {
      const days = deliveryDaysForCountry(code, node);
      const strong = node.querySelector('.regista-delivery-estimate__copy strong');
      if (strong) strong.textContent = `Tracked delivery to ${name}`;
      node.dataset.deliveryDays = String(days);
      node.dataset.detectedCountryCode = code;
      node.dataset.deliverySource = 'shopify-geoip';
      node.dataset.deliveryRegion = code === 'GB' ? 'uk' : code === 'US' ? 'us' : EUROPE_COUNTRY_CODES.has(code) ? 'europe' : 'rest';
      updateDeliveryDateCopy(node, days);
      updated = true;
    });

    return updated;
  };

  const readCountryFromBrowsingContext = (payload) => {
    // detected_values is Shopify's IP/browser result and is deliberately preferred over
    // suggestions, which describe an alternative storefront context rather than the raw
    // detected location.
    return normalizeDetectedCountry(payload?.detected_values?.country)
      || normalizeDetectedCountry(payload?.suggestions?.[0]?.parts?.country)
      || null;
  };

  const requestDetectedDeliveryCountry = async () => {
    const root = window.Shopify?.routes?.root;
    if (!root || typeof fetch !== 'function') return null;

    // Do not exclude the current Market on the first request. We need detected_values even
    // when Shopify thinks the currently selected Market is already valid.
    const base = `${root}browsing_context_suggestions.json?country[enabled]=true`;
    const requests = [
      `${base}&_=${Date.now()}`,
      `${base}&country[exclude]=${encodeURIComponent(String(window.Shopify?.country || ''))}&_=${Date.now() + 1}`
    ];

    for (const url of requests) {
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) continue;
        const payload = await response.json();
        const country = readCountryFromBrowsingContext(payload);
        if (country) return country;
      } catch (error) {
        // Try the second Shopify request before falling back to server localization.
      }
    }

    return null;
  };

  const detectDeliveryCountry = async (root = document, options = {}) => {
    const nodes = Array.from(root.querySelectorAll('[data-regista-delivery-estimate]'));
    if (!nodes.length) return null;

    const force = Boolean(options.force);
    const cached = getDetectedCountryCache();
    if (!force && cached?.handle) {
      applyDetectedDeliveryCountry(cached, root);
      return cached;
    }

    if (!force && registaDeliveryDetectionPromise) {
      const pendingCountry = await registaDeliveryDetectionPromise;
      if (pendingCountry) applyDetectedDeliveryCountry(pendingCountry, root);
      return pendingCountry;
    }

    registaDeliveryDetectionPromise = requestDetectedDeliveryCountry()
      .then((country) => {
        if (country) setDetectedCountryCache(country);
        return country;
      })
      .finally(() => {
        registaDeliveryDetectionPromise = null;
      });

    const detected = await registaDeliveryDetectionPromise;
    if (detected) {
      applyDetectedDeliveryCountry(detected, document);
      if (root !== document) applyDetectedDeliveryCountry(detected, root);
    }
    return detected;
  };


  const normalizeMatchToken = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  let registaProductIndexPromise = null;

  const getRegistaProductIndex = async (url) => {
    if (!url) return [];
    if (!registaProductIndexPromise) {
      registaProductIndexPromise = fetch(url, { credentials: 'same-origin', cache: 'force-cache', headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('Product index request failed');
          return response.json();
        })
        .then((payload) => Array.isArray(payload?.products) ? payload.products : [])
        .catch((error) => {
          registaProductIndexPromise = null;
          throw error;
        });
    }
    return registaProductIndexPromise;
  };

  const getRandomIndex = (max) => {
    if (!Number.isFinite(max) || max <= 1) return 0;
    try {
      if (window.crypto?.getRandomValues) {
        const values = new Uint32Array(1);
        window.crypto.getRandomValues(values);
        return values[0] % max;
      }
    } catch (error) {
      // Math.random remains a perfectly adequate fallback for merchandising rotation.
    }
    return Math.floor(Math.random() * max);
  };

  const shuffleRecommendationHandles = (handles) => {
    const shuffled = [...handles];
    for (let index = shuffled.length - 1; index > 0; index -= 1) {
      const swapIndex = getRandomIndex(index + 1);
      [shuffled[index], shuffled[swapIndex]] = [shuffled[swapIndex], shuffled[index]];
    }
    return shuffled;
  };

  const getSimilarRecommendationKey = (team, size, currentHandle) => [
    normalizeTeamMatchToken(team),
    normalizeMatchToken(size),
    String(currentHandle || '').trim().toLowerCase()
  ].join('::');

  const readPreviousSimilarHandles = (key) => {
    try {
      const value = window.sessionStorage.getItem(`regista-similar:${key}`);
      const parsed = value ? JSON.parse(value) : [];
      return Array.isArray(parsed) ? parsed.map((handle) => String(handle || '').toLowerCase()).filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  };

  const savePreviousSimilarHandles = (key, handles) => {
    try {
      window.sessionStorage.setItem(`regista-similar:${key}`, JSON.stringify(handles.slice(0, 3)));
    } catch (error) {
      // Recommendation rotation still works without storage; only repeat-avoidance is lost.
    }
  };

  const registaSimilarCandidateCache = new Map();

  const REGISTA_TEAM_MATCH_ALIASES = new Map([
    ['unitedstates', 'usa'],
    ['celticfc', 'celtic'],
    ['fulhamfc', 'fulham'],
    ['mallorcafc', 'mallorca'],
    ['parissaintgermain', 'parissaintgermainfc'],
    ['realsociedadfc', 'realsociedad'],
    ['caosasuna', 'osasuna'],
    ['cfamerica', 'clubamerica'],
    ['villareal', 'villarreal'],
    ['tsv1860munchen', '1860munich'],
    ['kvmechelen', 'mechelenfc'],
    ['realzaragoza', 'zaragoza'],
    ['realoviedo', 'oviedo'],
    ['paokthessaloniki', 'paokfc'],
    ['psveindhoven', 'psv'],
    ['newyorkrb', 'newyorkredbulls'],
    ['wolverhamptonwanderers', 'wolves'],
    ['scfreiburg', 'freiburg'],
    ['southamptonfc', 'southampton'],
    ['deportivoalaves', 'alaves']
  ]);

  const normalizeTeamMatchToken = (value) => {
    const token = normalizeMatchToken(value);
    return REGISTA_TEAM_MATCH_ALIASES.get(token) || token;
  };

  const isRegistaShirtIndexItem = (item) => {
    if (!item) return false;

    const typeKey = String(item.ty || '').trim().toLowerCase();
    const baseHandle = String(item.h || '').trim().toLowerCase();

    // Explicit shirt/jersey product types always win. This prevents club names such as
    // Cardiff from being rejected merely because their handle contains the word "card".
    if (typeKey === 'football shirt' || typeKey === 'heros' || typeKey === 'basketball jersey') return true;
    if (item.k === true) return true;

    // These catalog types are apparel, but they are not shirts for this recommendation block.
    if (typeKey === 'hoodie' || typeKey === 'jacket' || typeKey === 'training') return false;
    if (!baseHandle.includes('-size-')) return false;

    // Some older football shirts have a blank product type. For those records, use exact
    // slug tokens instead of broad substring matching so names like Cardiff remain valid.
    const handleTokens = new Set(baseHandle.split('-').filter(Boolean));
    const excludedTokens = ['hoodie', 'jacket', 'tracksuit', 'shorts', 'belt', 'sock', 'socks', 'cap', 'hat', 'poster', 'card', 'cards', 'patch', 'patches', 'subscription'];
    if (excludedTokens.some((token) => handleTokens.has(token))) return false;
    if (baseHandle.includes('mystery-box')) return false;

    return true;
  };

  const resolveSimilarProductContext = (products, currentHandle, initialTeam = '', initialSize = '') => {
    const currentKey = String(currentHandle || '').trim().toLowerCase();
    let team = String(initialTeam || '').trim();
    let size = String(initialSize || '').trim();
    if (!currentKey) return { team, size, item: null };

    const matchedItem = products.find((item) => {
      if (!isRegistaShirtIndexItem(item)) return false;
      if (String(item.h || '').trim().toLowerCase() === currentKey) return true;
      const sizeMap = item.hm && typeof item.hm === 'object' ? item.hm : {};
      return Object.values(sizeMap).some((handle) => String(handle || '').trim().toLowerCase() === currentKey);
    }) || null;

    if (!matchedItem) return { team, size, item: null };

    // The generated index is the canonical source for recommendation matching.
    // Liquid metafields remain useful as a fallback, but they no longer decide whether
    // a shirt receives recommendations.
    team = String(matchedItem.tm || team || '').trim();

    const sizeMap = matchedItem.hm && typeof matchedItem.hm === 'object' ? matchedItem.hm : {};
    const currentSizeEntry = Object.entries(sizeMap).find(([, handle]) => String(handle || '').trim().toLowerCase() === currentKey);
    if (currentSizeEntry) size = String(currentSizeEntry[0] || '').trim();

    return { team, size, item: matchedItem };
  };

  const findRegistaIndexItemByHandle = (products, currentHandle) => {
    const currentKey = String(currentHandle || '').trim().toLowerCase();
    if (!currentKey || !Array.isArray(products)) return null;

    return products.find((item) => {
      if (!item) return false;
      if (String(item.h || '').trim().toLowerCase() === currentKey) return true;
      const sizeMap = item.hm && typeof item.hm === 'object' ? item.hm : {};
      return Object.values(sizeMap).some((handle) => String(handle || '').trim().toLowerCase() === currentKey);
    }) || null;
  };

  const getIndexedSizeForHandle = (item, currentHandle) => {
    if (!item) return '';
    const currentKey = String(currentHandle || '').trim().toLowerCase();
    const sizeMap = item.hm && typeof item.hm === 'object' ? item.hm : {};
    const entry = Object.entries(sizeMap).find(([, handle]) => String(handle || '').trim().toLowerCase() === currentKey);
    return entry ? String(entry[0] || '').trim() : '';
  };

  const parseProductSeason = (value) => {
    const text = String(value || '').replace(/\u2013|\u2014/g, '-');
    let match = text.match(/\b((?:19|20)\d{2})\s*[-/]\s*((?:19|20)?\d{2})\b/);
    if (match) return `${match[1]}/${match[2]}`;

    match = text.match(/\b(\d{2})\s*[-/]\s*(\d{2})\b/);
    if (match) return `${match[1]}/${match[2]}`;

    match = text.match(/\b(\d{2})\s+(\d{2})\b/);
    if (match) return `${match[1]}/${match[2]}`;

    match = text.match(/\b((?:19|20)\d{2})\b/);
    return match ? match[1] : '';
  };

  const inferProductDetailType = (title, handle, indexedType = '', existingType = '') => {
    const key = `${existingType || ''} ${indexedType || ''} ${title || ''} ${handle || ''}`.toLowerCase();
    if (key.includes('jacket')) return 'Jacket';
    if (key.includes('goalkeeper')) return 'Goalkeeper Shirt';
    if (key.includes('fourth')) return 'Fourth Shirt';
    if (key.includes('third')) return 'Third Shirt';
    if (key.includes('away')) return 'Away Shirt';
    if (key.includes('european') || key.includes('champions league') || key.includes('champions-league') || key.includes('uefa') || key.includes('europa')) return 'European Shirt';
    if (key.includes('home')) return 'Home Shirt';
    if (key.includes('training')) return 'Training Shirt';
    if (key.includes('kids kit') || key.includes('kids-kit') || key.includes('signature jersey') || key.includes('football shirt') || key.includes('shirt') || key.includes('jersey')) return 'Football Shirt';
    return '';
  };

  const inferProductSleeve = (title, handle) => {
    const key = `${title || ''} ${handle || ''}`.toLowerCase();
    if (key.includes('long sleeve') || key.includes('long-sleeve')) return 'Long Sleeve';
    if (key.includes('short sleeve') || key.includes('short-sleeve')) return 'Short Sleeve';
    return '';
  };

  const setProductDetailField = (card, field, value) => {
    if (!card) return;
    const row = card.querySelector(`[data-regista-product-detail-row="${field}"]`);
    const output = card.querySelector(`[data-regista-product-detail-value="${field}"]`);
    const cleanValue = String(value || '').trim();
    if (!row || !output) return;

    if (!cleanValue) {
      row.hidden = true;
      return;
    }

    output.textContent = cleanValue;
    row.hidden = false;
  };

  const hydrateProductDetails = async (card) => {
    if (!card || card.dataset.registaHydrated === 'true' || card.dataset.registaHydrating === 'true') return;
    card.dataset.registaHydrating = 'true';

    const currentHandle = String(card.dataset.productHandle || '').trim();
    const title = String(card.dataset.productTitle || '').trim();
    const indexUrl = String(card.dataset.indexUrl || '').trim();

    const details = {
      team: String(card.dataset.initialTeam || '').trim(),
      season: String(card.dataset.initialSeason || '').trim(),
      type: String(card.dataset.initialType || '').trim(),
      size: String(card.dataset.initialSize || '').trim(),
      condition: String(card.dataset.initialCondition || '').trim(),
      player: String(card.dataset.initialPlayer || '').trim(),
      sleeve: String(card.dataset.initialSleeve || '').trim(),
      competition: String(card.dataset.initialCompetition || '').trim(),
      country: String(card.dataset.initialCountry || '').trim()
    };

    try {
      if (indexUrl && currentHandle) {
        const products = await getRegistaProductIndex(indexUrl);
        const item = findRegistaIndexItemByHandle(products, currentHandle);
        if (item) {
          if (!details.team) details.team = String(item.tm || '').trim();
          if (!details.player) {
            details.player = String(item.hr || '').trim();
            const numberMatch = String(item.e || title || '').match(/#\s*(\d{1,3})\b/);
            if (details.player && numberMatch && !details.player.includes('#')) details.player += ` #${numberMatch[1]}`;
          }
          if (!details.size) details.size = getIndexedSizeForHandle(item, currentHandle);
          if (!details.type) details.type = inferProductDetailType(item.e || title, currentHandle, item.ty || '', details.type || '');
          if (!details.season) details.season = parseProductSeason(item.e || title || currentHandle);
        }
      }
    } catch (error) {
      // The card is already useful from server-rendered listing data. Index enrichment is optional.
    }

    if (!details.season) details.season = parseProductSeason(title || currentHandle);
    details.type = inferProductDetailType(title, currentHandle, '', details.type || '');
    if (!details.sleeve) details.sleeve = inferProductSleeve(title, currentHandle);

    ['team', 'country', 'season', 'type', 'size', 'competition', 'condition', 'player', 'sleeve'].forEach((field) => {
      setProductDetailField(card, field, details[field]);
    });

    const summary = card.querySelector('[data-regista-product-details-summary]');
    if (summary) {
      const typePhrase = details.type ? details.type.toLowerCase() : 'football shirt';
      let copy = details.team ? `${details.team} ${typePhrase}` : (title || 'Football shirt');
      if (details.season) copy += ` from the ${details.season} season`;
      if (details.player) copy += ` featuring ${details.player}`;
      if (details.size) copy += ` in size ${details.size}`;
      if (details.condition) copy += `, listed as ${details.condition}`;
      summary.textContent = `${copy}.`;
    }

    card.dataset.registaHydrated = 'true';
    card.dataset.registaHydrating = 'false';
  };

  const hydrateAllProductDetails = (root = document) => {
    root.querySelectorAll('[data-regista-product-details]').forEach(hydrateProductDetails);
  };

  const findSameTeamSizeHandles = (products, team, size, currentHandle, limit = 3, previousHandles = []) => {
    const teamKey = normalizeTeamMatchToken(team);
    const sizeKey = normalizeMatchToken(size);
    const currentKey = String(currentHandle || '').trim().toLowerCase();
    if (!teamKey || !sizeKey) return [];

    const candidates = [];
    const seen = new Set([currentKey]);

    products.forEach((item) => {
      if (!isRegistaShirtIndexItem(item) || normalizeTeamMatchToken(item.tm) !== teamKey) return;

      const sizeMap = item.hm && typeof item.hm === 'object' ? item.hm : {};
      const sizeEntry = Object.entries(sizeMap).find(([key]) => normalizeMatchToken(key) === sizeKey);
      if (!sizeEntry) return;
      const handle = String(sizeEntry[1] || '').trim();
      const handleKey = handle.toLowerCase();
      if (!handle || seen.has(handleKey)) return;
      seen.add(handleKey);
      candidates.push(handle);
    });

    const previousSet = new Set(previousHandles.map((handle) => String(handle || '').toLowerCase()));
    const fresh = shuffleRecommendationHandles(candidates.filter((handle) => !previousSet.has(handle.toLowerCase())));
    const repeated = shuffleRecommendationHandles(candidates.filter((handle) => previousSet.has(handle.toLowerCase())));

    // Fresh matches lead the list, which guarantees a changed trio on refresh whenever
    // more than three valid same-team/same-size shirts exist. Previous items are kept
    // only as fallbacks so sparse collections can still fill all three slots.
    return [...fresh, ...repeated].slice(0, limit);
  };

  const buildSimilarProductFallbackCard = (productData, handle) => {
    if (!productData) return '';
    const variants = Array.isArray(productData.variants) ? productData.variants : [];
    const variant = variants.find((item) => item && item.available) || variants[0];
    if (!variant || !variant.available) return '';

    const root = window.Shopify?.routes?.root || '/';
    const productUrl = `${root}products/${encodeURIComponent(handle)}`;
    const imageUrl = productData.featured_image || productData.images?.[0] || '';
    const currency = window.Shopify?.currency?.active || 'GBP';
    let priceText = '';
    try {
      priceText = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency }).format(Number(variant.price || productData.price || 0) / 100);
    } catch (error) {
      priceText = `${(Number(variant.price || productData.price || 0) / 100).toFixed(2)} ${currency}`;
    }

    const article = document.createElement('article');
    article.className = 'regista-similar-card';
    article.dataset.registaSimilarCard = '';
    article.dataset.productHandle = handle;

    const imageLink = document.createElement('a');
    imageLink.className = 'regista-similar-card__image';
    imageLink.href = productUrl;
    imageLink.setAttribute('data-instant', '');
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = productData.title || '';
      image.loading = 'lazy';
      imageLink.appendChild(image);
    }

    const body = document.createElement('div');
    body.className = 'regista-similar-card__body';
    const copy = document.createElement('div');
    copy.className = 'regista-similar-card__copy';
    const title = document.createElement('a');
    title.className = 'regista-similar-card__title';
    title.href = productUrl;
    title.setAttribute('data-instant', '');
    title.textContent = productData.title || handle;
    const meta = document.createElement('div');
    meta.className = 'regista-similar-card__meta';
    const price = document.createElement('span');
    price.className = 'price-list sale-price';
    price.textContent = priceText;
    meta.appendChild(price);
    copy.append(title, meta);

    const productForm = document.createElement('product-form');
    productForm.className = 'regista-similar-card__form';
    const form = document.createElement('form');
    form.method = 'post';
    form.action = `${root}cart/add`;
    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = String(variant.id);
    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';
    const successInput = document.createElement('input');
    successInput.type = 'hidden';
    successInput.name = 'on_success';
    successInput.value = 'force_open_drawer';
    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'regista-similar-card__add';
    button.innerHTML = '<span aria-hidden="true">Add</span><span class="sr-only">Add to basket</span>';
    form.append(idInput, quantityInput, successInput, button);
    productForm.appendChild(form);
    body.append(copy, productForm);
    article.append(imageLink, body);
    return article.outerHTML;
  };

  const fetchSimilarProductCard = async (handle) => {
    const root = window.Shopify?.routes?.root || '/';
    const encodedHandle = encodeURIComponent(handle);
    const viewUrl = `${root}products/${encodedHandle}?view=regista-similar-card`;

    try {
      const response = await fetch(viewUrl, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
      if (response.ok) {
        const html = (await response.text()).trim();
        if (html) {
          const template = document.createElement('template');
          template.innerHTML = html;
          const card = template.content.querySelector('[data-regista-similar-card]');
          if (card) return card.outerHTML;
        }
      }
    } catch (error) {
      // Fall through to Shopify product JSON so a failed alternate view cannot remove the whole recommendation block.
    }

    const fallbackResponse = await fetch(`${root}products/${encodedHandle}.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    });
    if (!fallbackResponse.ok) throw new Error(`Similar product request failed: ${handle}`);
    return buildSimilarProductFallbackCard(await fallbackResponse.json(), handle);
  };

  const isProductSimilarViewportActive = (section) => {
    const viewport = String(section?.dataset?.registaViewport || 'all').toLowerCase();
    if (viewport === 'mobile') return window.matchMedia('(max-width: 999px)').matches;
    if (viewport === 'desktop') return window.matchMedia('(min-width: 1000px)').matches;
    return true;
  };

  const loadProductSimilar = async (section) => {
    if (!section || !isProductSimilarViewportActive(section) || section.dataset.loading === 'true' || section.dataset.loaded === 'true') return;
    const currentHandle = section.dataset.currentHandle || '';
    const indexUrl = section.dataset.indexUrl || '';
    const loading = section.querySelector('[data-regista-product-similar-loading]');
    const results = section.querySelector('[data-regista-product-similar-results]');
    const heading = section.querySelector('[data-regista-similar-heading]');
    const sizeLabel = section.querySelector('[data-regista-similar-size]');
    if (!currentHandle || !indexUrl || !results) {
      section.hidden = true;
      return;
    }

    section.dataset.loading = 'true';
    try {
      const products = await getRegistaProductIndex(indexUrl);
      const context = resolveSimilarProductContext(products, currentHandle, section.dataset.team || '', section.dataset.size || '');
      const team = context.team;
      const size = context.size;

      if (!team || !size || !context.item) {
        section.hidden = true;
        return;
      }

      section.dataset.team = team;
      section.dataset.size = size;
      if (heading) heading.textContent = `More ${team} shirts in this size`;
      if (sizeLabel) {
        sizeLabel.textContent = `Size ${size}`;
        sizeLabel.hidden = false;
      }

      const recommendationKey = getSimilarRecommendationKey(team, size, currentHandle);
      const previousHandles = readPreviousSimilarHandles(recommendationKey);

      // Keep one random candidate order for the current page load so desktop/mobile
      // render the same trio if the viewport changes, while a full refresh rotates it.
      let handles = registaSimilarCandidateCache.get(recommendationKey);
      if (!handles?.length) {
        handles = findSameTeamSizeHandles(products, team, size, currentHandle, 12, previousHandles);
        registaSimilarCandidateCache.set(recommendationKey, handles);
      }
      if (!handles.length) {
        section.hidden = true;
        return;
      }

      section.hidden = false;
      if (loading) loading.hidden = false;

      const settledCards = await Promise.allSettled(handles.map((handle) => fetchSimilarProductCard(handle)));
      const cardsWithHandles = settledCards
        .map((result, index) => ({ result, handle: handles[index] }))
        .filter(({ result }) => result.status === 'fulfilled' && result.value)
        .slice(0, 3);
      const cards = cardsWithHandles.map(({ result }) => result.value);

      if (!cards.length) {
        if (section.dataset.registaRetry !== 'true') {
          section.dataset.registaRetry = 'true';
          section.dataset.loading = 'false';
          if (loading) loading.hidden = false;
          window.setTimeout(() => loadProductSimilar(section), 900);
          return;
        }
        section.hidden = true;
        return;
      }

      results.innerHTML = cards.join('');
      results.hidden = false;
      section.dataset.loaded = 'true';
      savePreviousSimilarHandles(recommendationKey, cardsWithHandles.map(({ handle }) => handle));
    } catch (error) {
      console.warn('[Regista] Same-team recommendations could not load', error);
      if (section.dataset.registaRetry !== 'true') {
        section.dataset.registaRetry = 'true';
        section.dataset.loading = 'false';
        if (loading) loading.hidden = false;
        window.setTimeout(() => loadProductSimilar(section), 900);
        return;
      }
      section.hidden = true;
    } finally {
      section.dataset.loading = 'false';
      if (loading) loading.hidden = true;
    }
  };

  const loadAllProductSimilar = (root = document) => {
    if (root.matches?.('[data-regista-product-similar]')) loadProductSimilar(root);
    root.querySelectorAll?.('[data-regista-product-similar]').forEach(loadProductSimilar);
  };

  const parseCrossSellResponse = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    const response = template.content.querySelector('[data-regista-cross-sell-response]');
    return {
      count: Number(response?.dataset.count || 0),
      html: response?.innerHTML.trim() || ''
    };
  };

  const fetchCrossSell = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
    if (!response.ok) throw new Error('Cross-sell request failed');
    return parseCrossSellResponse(await response.text());
  };

  const mergeCrossSellResponses = (responses, limit = 4) => {
    const seen = new Set();
    const cards = [];

    responses.forEach((response) => {
      if (!response?.html || cards.length >= limit) return;
      const template = document.createElement('template');
      template.innerHTML = response.html;
      template.content.querySelectorAll('.regista-cross-sell-card').forEach((card) => {
        if (cards.length >= limit) return;
        const key = card.dataset.productId || card.querySelector('a[href]')?.getAttribute('href') || card.textContent.trim();
        if (!key || seen.has(key)) return;
        seen.add(key);
        cards.push(card.outerHTML);
      });
    });

    return { count: cards.length, html: cards.join('') };
  };

  const loadCrossSell = async (section) => {
    if (!section || section.dataset.loading === 'true' || section.dataset.loaded === 'true') return;
    const primaryUrl = section.dataset.primaryUrl;
    const fallbackUrl = section.dataset.fallbackUrl;
    if (!primaryUrl) return;

    section.dataset.loading = 'true';
    const loading = section.querySelector('[data-regista-cross-sell-loading]');
    const results = section.querySelector('[data-regista-cross-sell-results]');
    const manual = section.querySelector('[data-regista-cross-sell-manual]');
    const preserveHeading = section.dataset.bundleRemaining === '1';

    try {
      const primary = await fetchCrossSell(primaryUrl);
      let response = primary;
      let usedFallbackOnly = false;

      if (fallbackUrl && fallbackUrl !== primaryUrl && primary.count < 4) {
        const fallback = await fetchCrossSell(fallbackUrl);
        if (primary.count) {
          response = mergeCrossSellResponses([primary, fallback], 4);
        } else {
          response = fallback;
          usedFallbackOnly = Boolean(fallback.count);
        }
      }

      if (response.count && response.html) {
        if (usedFallbackOnly && section.dataset.fallbackTitle && !preserveHeading) {
          const heading = section.querySelector('[data-regista-cross-sell-heading]');
          if (heading) heading.textContent = section.dataset.fallbackTitle;
          section.setAttribute('aria-label', section.dataset.fallbackTitle);
        }
        results.innerHTML = response.html;
        results.hidden = false;
        if (manual) manual.hidden = true;
      } else if (manual?.innerHTML.trim()) {
        if (section.dataset.fallbackTitle && !preserveHeading) {
          const heading = section.querySelector('[data-regista-cross-sell-heading]');
          if (heading) heading.textContent = section.dataset.fallbackTitle;
          section.setAttribute('aria-label', section.dataset.fallbackTitle);
        }
        manual.hidden = false;
        results.hidden = true;
      } else {
        section.hidden = true;
      }
      section.dataset.loaded = 'true';
    } catch (error) {
      if (manual?.innerHTML.trim()) {
        if (section.dataset.fallbackTitle && !preserveHeading) {
          const heading = section.querySelector('[data-regista-cross-sell-heading]');
          if (heading) heading.textContent = section.dataset.fallbackTitle;
          section.setAttribute('aria-label', section.dataset.fallbackTitle);
        }
        manual.hidden = false;
      } else {
        section.hidden = true;
      }
    } finally {
      section.dataset.loading = 'false';
      if (loading) loading.hidden = true;
    }
  };

  const loadAllCrossSells = (root = document) => {
    root.querySelectorAll('[data-regista-cart-cross-sell]').forEach(loadCrossSell);
  };

  const observeDynamicCRO = () => {
    if (!document.body || typeof MutationObserver === 'undefined') return;

    const observer = new MutationObserver((mutations) => {
      const roots = [];
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('[data-regista-save-product], [data-regista-cart-cross-sell], [data-regista-product-similar]') || node.querySelector('[data-regista-save-product], [data-regista-cart-cross-sell], [data-regista-product-similar]')) {
            roots.push(node);
          }
        });
      });

      roots.forEach((root) => {
        if (root.matches('[data-regista-save-product]')) updateSaveButtons(root.parentElement || document);
        else updateSaveButtons(root);

        if (root.matches('[data-regista-cart-cross-sell]')) loadCrossSell(root);
        loadAllCrossSells(root);
        loadAllProductSimilar(root);
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const saveButton = event.target.closest('[data-regista-save-product]');
    if (saveButton) {
      event.preventDefault();
      event.stopPropagation();
      handleSaveClick(saveButton);
      return;
    }

    if (event.target.closest('[data-regista-save-close]')) closeModal();
  });

  document.addEventListener('submit', (event) => {
    if (!event.target.matches('#RegistaSaveAlertForm')) return;
    event.preventDefault();
    handleSaveForm(event.target);
  });

  document.addEventListener('keydown', (event) => {
    const modal = getModal();
    if (!modal || modal.hidden) return;

    if (event.key === 'Escape') {
      closeModal();
      return;
    }

    if (event.key !== 'Tab') return;
    const dialog = modal.querySelector('[data-regista-save-dialog]');
    if (!dialog) return;
    const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true');
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('variant:change', updateScarcity);
  document.addEventListener('cart-drawer:refreshed', (event) => {
    updateSaveButtons(event.target);
    loadAllCrossSells(event.target);
    loadAllProductSimilar(event.target);
    hydrateAllProductDetails(event.target);
    updateAllDeliveryDates(event.target);
    const detectedCountry = getDetectedCountryCache();
    if (detectedCountry) applyDetectedDeliveryCountry(detectedCountry, event.target);
    else detectDeliveryCountry(event.target);
  });
  document.addEventListener('shopify:section:load', (event) => {
    updateSaveButtons(event.target);
    loadAllCrossSells(event.target);
    loadAllProductSimilar(event.target);
    hydrateAllProductDetails(event.target);
    updateAllDeliveryDates(event.target);
    const detectedCountry = getDetectedCountryCache();
    if (detectedCountry) applyDetectedDeliveryCountry(detectedCountry, event.target);
    else detectDeliveryCountry(event.target);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) detectDeliveryCountry(document, { force: true });
  });

  window.addEventListener('online', () => {
    if (!getDetectedCountryCache()) detectDeliveryCountry(document, { force: true });
  });

  let registaSimilarResizeTimer = 0;
  window.addEventListener('resize', () => {
    window.clearTimeout(registaSimilarResizeTimer);
    registaSimilarResizeTimer = window.setTimeout(() => loadAllProductSimilar(), 120);
  }, { passive: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      updateSaveButtons();
      flushPendingDripEvents();
      window.setTimeout(flushPendingDripEvents, 2500);
      window.setTimeout(flushPendingDripEvents, 7000);
      loadAllCrossSells();
      loadAllProductSimilar();
      hydrateAllProductDetails();
      updateAllDeliveryDates();
      detectDeliveryCountry();
      window.setTimeout(() => { if (!getDetectedCountryCache()) detectDeliveryCountry(document, { force: true }); }, 1800);
      observeDynamicCRO();
    }, { once: true });
  } else {
    updateSaveButtons();
    flushPendingDripEvents();
    window.setTimeout(flushPendingDripEvents, 2500);
    window.setTimeout(flushPendingDripEvents, 7000);
    loadAllCrossSells();
    loadAllProductSimilar();
    hydrateAllProductDetails();
    updateAllDeliveryDates();
    detectDeliveryCountry();
    window.setTimeout(() => { if (!getDetectedCountryCache()) detectDeliveryCountry(document, { force: true }); }, 1800);
    observeDynamicCRO();
  }
})();
